import type * as Referrals from "../../lib/models/referrals.server.ts"

import type { MoneyV2 } from "@shopify/hydrogen/storefront-api-types"
import type { ProductVariantFragment } from "../../lib/shopify/types/storefront.generated"

import * as UI from "@iyk/ui"
import * as React from "react"
import * as Shopify from "../../lib/shopify/shopify.ts"

import { toSentenceCase } from "@iyk/string"
import { CartForm } from "@shopify/hydrogen"
import { useLang } from "../../lib/lang/use-lang.ts"
import { ItemMedia } from "../profiles/item-media.tsx"
import { CART_FETCHERS } from "./cart-fetchers.ts"
import { ProductPrice } from "./product-price.tsx"
import { useAddToCart, type UseAddToCartReturnType } from "./use-add-to-cart.ts"

// #region Context

const ShopContext = React.createContext<ShopContextType | null>(null)

/**
 * Provides shop context to children components
 * @param children - The child components to wrap
 * @param shopifyProduct - The Shopify product with variants
 * @returns The shop context provider
 */
const ShopProvider = ({ children, shopifyProduct, referral }: ShopProps) => {
  const variants = shopifyProduct.variants.nodes

  const [selectedOptions, setSelectedOptions] = React.useState(
    Shopify.getDefaultVariant(variants).selectedOptions,
  )

  const selectedVariant = React.useMemo(
    () => Shopify.getVariantFromOptions({ variants, options: selectedOptions }),
    [variants, selectedOptions],
  )

  const optionsWithMultipleValues = React.useMemo(
    () => shopifyProduct.options.filter((option) => option.optionValues.length > 1),
    [shopifyProduct],
  )

  const onOptionSelect: OnOptionSelect = (option) => {
    setSelectedOptions((prev) => [...prev.filter((o) => o.name !== option.name), option])
  }

  return (
    <ShopContext.Provider
      value={{
        shopifyProduct,
        referral,
        selectedOptions,
        selectedVariant,
        optionsWithMultipleValues,
        onOptionSelect,
      }}
    >
      {children}
    </ShopContext.Provider>
  )
}

/**
 * Hook to access shop context
 * @returns The shop context
 */
const useShop = () => {
  const context = React.useContext(ShopContext)
  if (!context) {
    throw new Error("Shop context is not provided")
  }
  return context
}

type OnOptionSelect = (option: Shopify.ProductOption) => void

type ShopContextType = {
  shopifyProduct: ShopProps["shopifyProduct"]
  referral?: Referrals.ReferralData | null
  selectedOptions: Shopify.ProductOption[]
  selectedVariant: ProductVariantFragment
  optionsWithMultipleValues: (Pick<Shopify.ProductOption, "name"> & {
    optionValues: Array<Pick<Shopify.ProductOptionWithValues, "name">>
  })[]
  onOptionSelect: OnOptionSelect
}

type ShopProps = {
  shopifyProduct: Shopify.ProductWithVariants
  referral?: Referrals.ReferralData | null
  children: React.ReactNode
}

// #endregion

// #region Root

/**
 * Root component for the shop context
 * @param shopifyProduct - The Shopify product with variants
 * @param children - The child components to wrap
 * @returns The shop context provider
 */
const Root = ({ shopifyProduct, referral, children }: ShopProps) => {
  return (
    <ShopProvider shopifyProduct={shopifyProduct} referral={referral}>
      {children}
    </ShopProvider>
  )
}

// #endregion

// #region Product Price

/**
 * Renders the product price with optional compare at price
 * @param size - The size of the price text
 * @param props - Additional props passed to container div
 * @returns The product price with optional compare at price
 */
const Price = ({
  ...props
}: Omit<React.ComponentProps<typeof ProductPrice>, "price" | "compareAtPrice">) => {
  const { selectedVariant, referral } = useShop()

  const { price, compareAtPrice } = selectedVariant

  if (referral && referral.discountPercentageDecimal > 0) {
    const priceWithReferralDiscount = createReferralPrice(price, referral)
    return <ProductPrice price={priceWithReferralDiscount} compareAtPrice={price} {...props} />
  }

  return <ProductPrice price={price} compareAtPrice={compareAtPrice} {...props} />
}

function createReferralPrice(price: MoneyV2, referral: Referrals.ReferralData): MoneyV2 {
  return {
    amount: (
      Number(price.amount) -
      Number(price.amount) * (referral.discountPercentageDecimal / 100)
    ).toFixed(2),
    currencyCode: price.currencyCode,
  }
}

// #endregion

// #region Container

const Container = ({ children, className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div className={UI.cx("flex flex-col gap-6", className)} {...props}>
      {children}
    </div>
  )
}

// #endregion

// #region Variants

/**
 * Renders product variant options like size, color etc. Can be customized with render prop.
 * @param className - Optional CSS class name to apply to container
 * @param children - Optional render prop to customize variant option rendering
 * @param props - Additional props passed to container div
 * @returns Container with either custom rendered or default variant options
 */
const Variants = ({
  className,
  children,
  ...props
}: {
  className?: string
  children?: (items: Shopify.ProductOptionWithValues[]) => React.ReactNode
} & React.ComponentProps<"div">) => {
  const shopContext = useShop()

  if (shopContext.optionsWithMultipleValues.length === 0) return null

  return (
    <div className={UI.cx("flex flex-col gap-6", className)} {...props}>
      {children
        ? children(shopContext.optionsWithMultipleValues)
        : shopContext.optionsWithMultipleValues.map((option) => (
            <VariantsItem key={option.name} option={option} />
          ))}
    </div>
  )
}

/**
 * Renders a single variant option group (e.g. Size, Color) with selectable values
 * @param option - The product option containing name and available values
 * @param props - Additional props passed to container div
 * @returns A group of selectable variant option values with availability indicators
 */
const VariantsItem = ({
  option,
  className,
  ...props
}: { option: Shopify.ProductOptionWithValues } & React.ComponentProps<"div">) => {
  const { shopifyProduct, selectedOptions, onOptionSelect } = useShop()
  const variants = shopifyProduct.variants.nodes

  return (
    <div key={option.name} className={UI.cx("flex flex-col gap-2", className)} {...props}>
      <span className="text-xs">{option.name}</span>
      <div className="flex flex-wrap gap-2">
        {option.optionValues.map((value) => {
          const isActive = Boolean(
            selectedOptions.find(
              (o) =>
                Shopify.getOptionKey(o) ===
                Shopify.getOptionKey({ name: option.name, value: value.name }),
            ),
          )

          const computedVariant = Shopify.getVariantFromOptions({
            variants,
            options: [
              // Check the option availability given the selection of the other options
              ...selectedOptions.filter((o) => o.name !== option.name),
              { name: option.name, value: value.name },
            ],
          })

          const isAvailable = Boolean(computedVariant?.availableForSale)

          return (
            <button
              key={option.name + value.name}
              type="button"
              onClick={() => onOptionSelect({ name: option.name, value: value.name })}
              disabled={!isAvailable}
              className={classForOptionSelectorButton({ isActive })}
            >
              {value.name}
              {!isAvailable && (
                <span className="absolute inset-x-0 top-1/2 h-[1px] -translate-y-1/2 bg-gray-6"></span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const classForOptionSelectorButton = UI.cva(
  [
    "relative",
    "px-2 py-1 text-sm",
    "bshadow-b rounded-none",
    "transition-colors duration-200",
    "enabled:hover:bshadow-gray-12",
    "disabled:text-gray-9 disabled:cursor-not-allowed",
  ],
  {
    variants: {
      isActive: {
        true: "bshadow-gray-12 bg-gray-4",
        false: "bshadow-gray-6",
      },
    },
  },
)

// #endregion

// #region Add to Cart Button

/**
 * Button component for adding items to cart
 * @param children Optional render prop that receives shop context and returns button content
 * @param props - Additional props passed to button
 * @returns Add to cart button that submits selected variant to cart
 */
const AddToCartButton = ({
  children,
  className,
  disabled,
  onAddToCartComplete,
  ...props
}: {
  children?: (args: ShopContextType) => React.ReactNode
  onAddToCartComplete?: (cart: UseAddToCartReturnType) => void
} & Omit<React.ComponentProps<"button">, "children" | "type">) => {
  const shopContext = useShop()
  const cart = useAddToCart()
  const lang = useLang()

  const isDisabled =
    disabled || !shopContext.selectedVariant || !shopContext.selectedVariant.availableForSale

  const defaultLabel = toSentenceCase(
    shopContext.selectedVariant.availableForSale ? lang.terms.ADD_TO_CART : lang.terms.SOLD_OUT,
  )

  const line = shopContext.selectedVariant
    ? {
        merchandiseId: shopContext.selectedVariant.id,
        quantity: 1,
        selectedVariant: shopContext.selectedVariant,
      }
    : null

  React.useEffect(() => {
    if (cart.addedVariant && onAddToCartComplete) {
      onAddToCartComplete(cart)
    }
  }, [cart.addedVariant])

  return (
    <CartForm
      route="/cart"
      inputs={{
        lines: line ? [line] : [],
        referralSource: shopContext.referral?.source,
      }}
      action={CartForm.ACTIONS.LinesAdd}
      fetcherKey={CART_FETCHERS.ADD_LINES}
    >
      {(fetcher) => {
        const isSubmitting = fetcher.state !== "idle"
        return (
          <UI.Button
            type="submit"
            kind="solid"
            size="lg"
            className={UI.cx("w-full", className)}
            disabled={isDisabled || isSubmitting}
            {...props}
          >
            {isSubmitting ? <UI.Icon.Loading /> : children ? children(shopContext) : defaultLabel}
          </UI.Button>
        )
      }}
    </CartForm>
  )
}

// #endregion

// #region Media

/**
 * Renders the media of the selected variant
 * @param props - Additional props passed to media component
 * @returns The media of the selected variant
 */
const Media = (props: Omit<React.ComponentProps<typeof ItemMedia>, "src" | "alt">) => {
  const { selectedVariant } = useShop()
  return (
    <ItemMedia
      src={selectedVariant.image?.url}
      alt={selectedVariant.image?.altText ?? ""}
      {...props}
    />
  )
}

// #endregion

// #region Exports

export const Shop = {
  Root,
  Container,
  Price,
  Variants,
  AddToCartButton,
  Media,
}

export { useShop }

// #endregion
